/**
 * §8.10 FAQs — tabbed by category, each tab has an accordion.
 */

import React, { useState } from 'react'
import clsx from 'clsx'
import {
  ServerCog, Bot, LineChart, Building2, Tag as TagIcon, CreditCard,
  ShieldCheck, UserCog, ChevronDown, HelpCircle, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { FAQ_GROUPS } from '../data'
import { TONE } from '../_shared'

const ICONS = { ServerCog, Bot, LineChart, Building2, TagIcon, CreditCard, ShieldCheck, UserCog }

export default function FAQs() {
  const [tab, setTab] = useState(FAQ_GROUPS[0].id)
  const [open, setOpen] = useState(0)
  const active = FAQ_GROUPS.find((g) => g.id === tab) || FAQ_GROUPS[0]

  return (
    <section id="faqs" className="relative py-24 md:py-28 scroll-mt-24">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>FAQs</Eyebrow>
          <SectionHeading align="center"
            title={<>The questions{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">buyers ask most.</span></>}
            subtitle="Organised by topic — platform, AI, trading, white-label, pricing, billing, security and account."
          />
        </div>

        {/* tabs */}
        <div className="flex flex-wrap gap-1.5">
          {FAQ_GROUPS.map((g) => {
            const Icon = ICONS[g.iconKey] || Sparkles
            const on = tab === g.id
            const t = TONE[g.tone] || TONE.orange
            return (
              <button key={g.id} onClick={() => { setTab(g.id); setOpen(0); }}
                className={clsx('inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11.5px] font-semibold uppercase tracking-wider transition-colors',
                  !on && 'border-white/10 bg-white/[0.03] text-zinc-400 hover:text-white',
                )}
                style={on ? { borderColor: `${t.hex}66`, background: `${t.hex}20`, color: t.hex } : undefined}>
                <Icon strokeWidth={2} style={{ width: 13, height: 13 }} />
                {g.label}
              </button>
            )
          })}
        </div>

        {/* accordion */}
        <div className="flex flex-col gap-2">
          {active.items.map((r, i) => {
            const isOpen = open === i
            return (
              <div key={i} className={clsx('rounded-2xl border transition-all',
                isOpen ? 'border-white/20 bg-white/[0.04]' : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.03]',
              )}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} aria-expanded={isOpen}
                  className="w-full flex items-center gap-3 p-4 md:p-5 text-left">
                  <HelpCircle className={clsx('h-4 w-4 shrink-0', isOpen ? 'text-brand-gold' : 'text-zinc-500')} />
                  <span className={clsx('flex-1 text-[15px] font-semibold', isOpen ? 'text-white' : 'text-zinc-200')}>{r.q}</span>
                  <ChevronDown className={clsx('h-4 w-4 text-zinc-500 shrink-0 transition-transform', isOpen && 'rotate-180')} />
                </button>
                {isOpen && (
                  <div className="px-4 md:px-5 pb-4 md:pb-5 -mt-1">
                    <p className="pl-7 text-sm text-zinc-300 leading-relaxed">{r.a}</p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
